import { useContext } from 'react';
import { BaseWidget } from './BaseWidget';
import GroupsIcon from '@mui/icons-material/GroupsOutlined';
import { SelectControl } from '../controls/SelectControl';
import { ColorControl } from '../controls/ColorControl';
import { FontControl } from '../controls/grouped/FontControl';
import { client } from '../../../../hooks/Client';
import { useFirestoreGeneral } from '../../../../firebase/useFirestore';
import { TemplateDataContext } from '../context/TemplateDataContext';
import { fetchTemplateData } from '../utils/fetchTemplateData';
import WidgetSourcePlaceholder from '../components/WidgetSourcePlaceholder';
import LoadingSpinner from '../../../common/LoadingSpinner';
import notDeleted from '../../../../helpers/notDeleted';
import { definitionCollection } from '../../../../firebase/publicDefinitions';

/**
 * StakeholderWidget — picks one target group (Stakeholders) and renders its
 * name + description. Data resolves per mode, mirroring the other data widgets:
 *   - template preview → templateData.data.targetgroups (exportTemplateData gatherer)
 *   - live (editor/own tenant) → Firestore `Stakeholders` by ID
 *
 * Built for the v1 → v2 report conversion: target groups are v1's most-used
 * section (86 reports), and copying their text at conversion time would freeze
 * it — resolving by ID keeps the report in step with the target group.
 * See docs/features/livereport-v1-to-v2-conversion.md
 *
 * @extends {BaseWidget}
 */
export class StakeholderWidget extends BaseWidget {
    getName() { return 'Doelgroep'; }
    getCategory() { return 'research'; }
    getIcon() { return <GroupsIcon />; }

    getControls() {
        return {
            stakeholderId: new SelectControl({
                label: 'Doelgroep',
                description: 'Kies een doelgroep',
                defaultValue: '',
                options: async () => {
                    const isTemplate = new URLSearchParams(window.location.search).get('template') === 'true';
                    let values = [];

                    if (isTemplate) {
                        const blob = await fetchTemplateData();
                        values = blob?.data?.targetgroups || [];
                    } else {
                        /* Equality + orderBy on a different field needs a composite
                           index that Stakeholders doesn't have, so sort client-side. */
                        const snap = await definitionCollection('Stakeholders')
                            .where('CompagnyID', '==', client || '')
                            .get();
                        values = snap.docs
                            .map(doc => doc.data())
                            .filter(notDeleted)
                            .sort((a, b) => (a.Position ?? 0) - (b.Position ?? 0));
                    }

                    return {
                        choices: values.map(s => ({ value: s.ID, label: s.Organisation || 'Doelgroep' })),
                        allowSearch: true,
                    };
                },
            }),
            font: new FontControl({
                label: 'Lettertype',
                description: 'Instellingen voor het lettertype van de doelgroep',
                defaultValue: {
                    fontSize: { value: 16, unit: 'px' },
                    alignment: 'left',
                },
            }),
            color: new ColorControl({
                label: 'Kleur',
                description: 'Kleur van de tekst',
                defaultValue: '#000000',
            }),
        };
    }

    render(props) {
        return <Renderer {...props} />;
    }
}

/**
 * @param {object} props
 * @param {string} props.stakeholderId - Selected Stakeholders ID
 * @param {object} [props.font] - FontControl value
 * @param {string} [props.color] - Text colour
 */
const Renderer = ({ stakeholderId, font, color }) => {
    const { templateData, isTemplate, error: templateError } = useContext(TemplateDataContext);

    const liveStakeholders = useFirestoreGeneral('Stakeholders', 'ID', isTemplate ? '' : (stakeholderId || ''));
    const stakeholder = isTemplate
        ? (templateData?.data?.targetgroups || []).find(s => s.ID === stakeholderId)
        : (Array.isArray(liveStakeholders) && liveStakeholders[0]);

    if (!stakeholder) {
        /* Template blob failed to load — show the error, never an endless spinner. */
        if (isTemplate && templateError) {
            return <p className="output-widget-error">Fout bij laden template data.</p>;
        }
        /* Still resolving (hook hasn't answered / template blob loading) →
           spinner; resolved-but-missing (target group deleted since the report
           was converted) or nothing selected → editor-only select CTA. */
        const stillResolving = isTemplate
            ? !templateData
            : (stakeholderId && !Array.isArray(liveStakeholders));
        if (stillResolving) return <LoadingSpinner />;
        return <WidgetSourcePlaceholder ctaLabel="Selecteer doelgroep" />;
    }

    /* Children are <span>: layout.css sets `color: var(--ink)` on bare h3/p/div,
       which beats an inherited colour and would silently kill the colour control.
       Jest can't catch that — CRA swaps every stylesheet for an empty object.
       AI-CONTEXT: See docs/pitfalls.md#ui-scoped-rule-lands-on-a-global-element-rule */
    const style = {
        textAlign: font?.alignment,
        fontFamily: font?.fontFamily,
        fontSize: font?.fontSize && `${font.fontSize.value}${font.fontSize.unit}`,
        fontStyle: font?.fontStyle,
        fontWeight: font?.fontWeight,
        textDecoration: font?.textDecoration,
        color: color,
    };

    return (
        <div className="livereport-stakeholder" style={style}>
            <span className="livereport-stakeholder-name">{stakeholder.Organisation}</span>
            {stakeholder.Description && (
                <span className="livereport-stakeholder-description">{stakeholder.Description}</span>
            )}
        </div>
    );
};
